import * as React from "react";
import type { AddInHistoryMessage, AddInHistorySuggestion, SaveAndInsertSuggestionArgs } from "../../types/chat";
import { ChatAssistantMarkdown } from "../ChatAssistantMarkdown";
import { CennaLogo } from "../primitives/Avatar";
import { ChatSuggestionCard } from "./ChatSuggestionCard";
import { ChatTypingIndicator } from "./ChatTypingIndicator";
import { MessageFooter } from "./ChatUserBubble";
import { ChatOutlineBtn } from "./ui/ChatOutlineBtn";
import { IconReload } from "./icons/ChatIcons";

export type ChatAssistantBubbleProps = {
  message: AddInHistoryMessage;
  messageIndex: number;
  isLatestAssistant: boolean;
  loading: boolean;
  retrying: boolean;
  hasSelectedText: boolean;
  onInsertSuggestion: (suggestion: AddInHistorySuggestion, suggestionIndex: number) => void | Promise<void>;
  onSaveAndInsertSuggestion: (args: SaveAndInsertSuggestionArgs) => Promise<void>;
  onRetryAssistantMessage?: (message: AddInHistoryMessage) => void | Promise<void>;
  editingKey: string | null;
  editedTextByKey: Record<string, string>;
  busyKey: string | null;
  onEnterEdit: (key: string, plain: string) => void;
  onExitEdit: (key: string) => void;
  onEditedTextChange: (key: string, value: string) => void;
  onBusy: (key: string | null) => void;
};

export const ChatAssistantBubble: React.FC<ChatAssistantBubbleProps> = ({
  message: m,
  messageIndex,
  isLatestAssistant,
  loading,
  retrying,
  hasSelectedText,
  onInsertSuggestion,
  onSaveAndInsertSuggestion,
  onRetryAssistantMessage,
  editingKey,
  editedTextByKey,
  busyKey,
  onEnterEdit,
  onExitEdit,
  onEditedTextChange,
  onBusy,
}) => {
  const canRetry =
    Boolean(onRetryAssistantMessage) &&
    isLatestAssistant &&
    !loading &&
    !m.id.startsWith("sse:");
  // Hide empty placeholder bubbles until the first tokens / suggestions arrive.
  // Bottom transcript loader covers the waiting state.
  if (
    !retrying &&
    !m.content.trim() &&
    m.suggestions.length === 0 &&
    (loading || m.id.startsWith("sse:") || m.id.startsWith("pending:"))
  ) {
    return null;
  }

  return (
    <div className="chat-ai-row">
      <div className="chat-ai-row__logo">
        <CennaLogo size={16} />
      </div>
      <div className="chat-ai-row__body">
        {retrying ? (
          <ChatTypingIndicator retrying />
        ) : (
          <>
            <div className="chat-ai-bubble">
              <ChatAssistantMarkdown text={m.content} density="chat" />
            </div>
            {m.content ? (
              <MessageFooter align="left" createdAt={m.createdAt} copyText={m.content} />
            ) : null}

            {m.suggestions.length > 0 ? (
              <div className="chat-suggestions">
                <span className="chat-suggestions__title">Suggestions</span>
                <div className="chat-suggestions__list">
                  {m.suggestions.map((s, index) => (
                    <ChatSuggestionCard
                      key={`${m.id}-s-${index}`}
                      messageId={m.id}
                      messageIndex={messageIndex}
                      suggestionIndex={index}
                      suggestion={s}
                      showActionBar={hasSelectedText && isLatestAssistant && !loading}
                      editingKey={editingKey}
                      editedTextByKey={editedTextByKey}
                      busyKey={busyKey}
                      onEnterEdit={onEnterEdit}
                      onExitEdit={onExitEdit}
                      onEditedTextChange={onEditedTextChange}
                      onBusy={onBusy}
                      onInsertSuggestion={onInsertSuggestion}
                      onSaveAndInsertSuggestion={onSaveAndInsertSuggestion}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {canRetry ? (
              <div className="chat-retry-bar">
                <ChatOutlineBtn onClick={() => void onRetryAssistantMessage?.(m)}>
                  <IconReload size={12} />
                  Retry
                </ChatOutlineBtn>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
